import axios from "axios";
import {BackgroundMetadata, PreviewMetadata} from "../common/ui/riamap/model";
import {ConfigurationSets, DBConnectionConfig, GeoprocessingServiceSettings} from "../settings/model";
import * as paths from "../paths";
import {
  AOIMonitor,
  AOINotificationArea,
  AOINotificationEvent,
  AOINotificationEventDetails
} from "../aoiNotifications/model";
import {AnalyticsRequest, AnalyticsResponse} from "../analytics/model";
import {AddResponse, Api} from "./InternalApi";
import {ControlRoomApiClass} from "./StudioInternalApi";
import {AccessRule, ServerConfiguration, SpatialModel} from "../model";
import {
  FeatureTypePropertyType,
  FeatureTypesProperties,
  Service,
  CatalogUpdatingMessageType,
} from "../services/model";
import {stringToXml} from "../common/util/Util";
import {APOLLO_CATALOG_ROOT_FOLDER, BatchProperties, ImportedData, NewDataRoot, NewFileInfo} from "../data/model";
import {Role, User} from "../user/model";
import {WFSCapabilities} from "@luciad/ria/model/capabilities/WFSCapabilities.js";
import {
  localRepository,
  PREVIEW_BACKGROUND_BOUNDS,
  PREVIEW_BACKGROUND_CRS,
  PREVIEW_BACKGROUND_LAYERNAME,
  PREVIEW_BACKGROUND_SUBDOMAINS,
  PREVIEW_BACKGROUND_TYPE,
  PREVIEW_BACKGROUND_URL,
} from "../common/LocalRepository";
import {convertSecurityFormAccessRuleToAccessRule, PermissionsType, SecurityFormAccessRule} from "../security/model";
import {ITEM_HAS_NO_RIGHTS} from "../security/SecurityForm";
import {oidcProviderInfo} from "../oidc/startOidcProvider";
import {ColorRamp, IchConfig} from "../styles/model";
import {Bounds} from "@luciad/ria/shape/Bounds.js";
import {createBounds} from "../common/ui/riamap/RiaApi";
import {oidcUtils} from "../oidc/utils/OidcUtils";
import pLimit from "p-limit";
import {SLDStyle} from "../styleEditor/model";
import {PendingProductForService} from "../products/model";

export class ApolloApiClass extends ControlRoomApiClass implements Api {

  getStudioSettings = (): Promise<any> => {
    return axios.get(`${this.getStudioApiUrl()}/configuration`)
        .then((r) => {
          return r;
        })
        .catch((error) => {
          throw error;
        });
  };

  saveStudioSettings = (settings: any) => {
    return axios.post(`${this.getStudioApiUrl()}/configuration`, settings)
        .then(_ => null);
  };

  getStudioSettingsDescription = (): Promise<ConfigurationSets> => {
    return axios.get(`${this.getStudioApiUrl()}/configuration/description`)
        .then((r) => {
          const config: ConfigurationSets = r.data;
          config.configurationSets.forEach(set => {
            set.id = encodeURIComponent(set.name);
          });
          return config;
        })
        .catch((error) => {
          throw error;
        });
  };

  getGeoprocessingServiceSettings = (): Promise<GeoprocessingServiceSettings> => {
    return axios.get(`${this.getStudioApiUrl()}/configuration`)
        .then((r) => {
          const result = r.data.values as { [key: string]: string };
          return {
            geoprocessingServerUrl: result["desGeoprocessingEngineUrl"],
          };
        });
  };

  getFusionDatastoreDirectory = (): Promise<string> => {
    return axios.get(`${this.getStudioApiUrl()}/configuration`)
        .then((r) => {
          const result = r.data.values as { [key: string]: string };
          return result["fusionDatastoreDirectory"];
        }).catch(() => {
          return "C:\\"
        });
  };

  isGeoprocessingServiceUrlValid = (geoprocessingRootUrl: string): Promise<boolean> => {
    // For OIDC, we use axios interceptors to add the authorization header for all requests.
    // This request fails with CORS error because an authorization header is not expected.
    // For this one request, we are creating an instance of axios so that the interceptor will not be used.
    const unInterceptedAxiosInstance = axios.create();
    return unInterceptedAxiosInstance.get(`${geoprocessingRootUrl}/oapi-p/`, {
      withCredentials: false,
      timeout: 5000
    });
  };

  loadAOINotifications = (): Promise<AOIMonitor[]> => {
    return axios({
      method: "GET",
      url: `${this.getStudioApiUrl()}/notification/aoi?profile=full`,
    })
        .then((r) => {
          return (r.data || []) as AOIMonitor[];
        });
  }

  createAOINotification = (monitor: AOIMonitor): Promise<void> => {
    return axios({
      method: "POST",
      url: `${this.getStudioApiUrl()}/notification/aoi`,
      data: {
        username: monitor.username,
        email: monitor.email,
        schedule: monitor.schedule,
        enabled: monitor.enabled,
        triggers: monitor.triggers,
      },
    })
        .then(_ => null);
  };

  deleteAOINotification = (monitorUserId: string): Promise<void> => {
    return axios({
      method: "DELETE",
      url: `${this.getStudioApiUrl()}/notification/aoi/${monitorUserId}`,
    })
        .then(_ => null);
  };

  deleteAOINotificationArea = (monitor: AOIMonitor, trigger: AOINotificationArea): Promise<void> => {
    return axios({
      method: "DELETE",
      url: `${this.getStudioApiUrl()}/notification/aoi/${monitor.id}/trigger/${trigger.id}`,
    })
        .then(_ => null);
  };

  updateAOINotification = (monitor: AOIMonitor, notificationArea?: Partial<AOINotificationArea>): Promise<void> => {
    return Promise.resolve()
        .then(() => {
          if (notificationArea != null && !notificationArea?.id) {
            //Create aoi
            return axios({
              method: "POST",
              url: `${this.getStudioApiUrl()}/notification/aoi/${monitor.id}/trigger`,
              data: {
                name: notificationArea.name,
                srs: notificationArea.srs,
                geomWkt: notificationArea.geomWkt,
              },
            });
          } else if (notificationArea != null && notificationArea?.id) {
            //update trigger
            return axios({
              method: "PATCH",
              url: `${this.getStudioApiUrl()}/notification/aoi/${monitor.id}/trigger/${notificationArea.id}`,
              data: {
                name: notificationArea.name,
                srs: notificationArea.srs,
                geomWkt: notificationArea.geomWkt,
              },
            });
          } else {
            //update monitor
            const trigs = monitor.triggers;
            trigs.forEach((trigger) => {
              delete trigger["id"];
              delete trigger["monitorUserId"];
            });

            return axios({
              method: "PATCH",
              url: `${this.getStudioApiUrl()}/notification/aoi/${monitor.id}`,
              data: {
                email: monitor.email,
                username: monitor.username,
                schedule: monitor.schedule,
                enabled: monitor.enabled,
                triggers: trigs,
              },
            });
          }
        })
        .then(_ => null);
  };

  getMetadataParsers = (): Promise<any> => {
    return axios({
      method: "GET",
      url: `${this.getStudioApiUrl()}/data/metadata-parsers`,
    }).then(r => {
      return r.data;
    });
  }

  getNotificationEvents = (monitorUserId: string, areaId: string): Promise<AOINotificationEvent[]> => {
    return axios({
      method: "GET",
      url: `${this.getStudioApiUrl()}/notification/aoi/${monitorUserId}/event?whatToInclude=all`,
    }).then(r => {
      return r.data.filter(a => a.triggerId === areaId) || [];
    });
  };

  getNotificationEventDetails = (eventId: string, monitorUserId: string): Promise<AOINotificationEventDetails> => {
    return axios({
      method: "GET",
      url: `${this.getStudioApiUrl()}/notification/aoi/${monitorUserId}/event/${eventId}`,
    }).then(r => {
      return r.data;
    });
  };

  updateNotificationEvent = (event: AOINotificationEvent): Promise<void> => {
    return axios({
      method: "PATCH",
      url: `${this.getStudioApiUrl()}/notification/aoi/${event.monitorUserId}/event`,
      data: {
        "readStatus": event.hasBeenRead,
        "eventIds": [
          event.id
        ]
      }
    }).then(_ => null);
  };

  deleteNotificationEvent = (monitorUserId: string, eventIds: string[]): Promise<void> => {
    return axios({
      method: "DELETE",
      url: `${this.getStudioApiUrl()}/notification/aoi/${monitorUserId}/event`,
      data: eventIds,
    }).then(_ => null);
  };

  getPreviewBackground = (): BackgroundMetadata => {
    if (localRepository != null) {
      let bounds: Bounds = null;
      try {
        const boundsA = localRepository.getParameter(PREVIEW_BACKGROUND_BOUNDS).split(',');
        if (boundsA.length === 4) {
          bounds = createBounds({
            x: Number(boundsA[0]),
            width: Number(boundsA[1]),
            y: Number(boundsA[2]),
            height: Number(boundsA[3])
          });
        }
      } catch (_e) {
        bounds = null;
      }

      const bgMetadata: BackgroundMetadata = {
        type: localRepository.getParameter(PREVIEW_BACKGROUND_TYPE),
        crs: localRepository.getParameter(PREVIEW_BACKGROUND_CRS),
        bounds
      };
      if (localRepository.getParameter(PREVIEW_BACKGROUND_URL) !== null &&
          localRepository.getParameter(PREVIEW_BACKGROUND_URL) !== "undefined") {
        switch (bgMetadata.type) {
        case "wms":
          bgMetadata.url = localRepository.getParameter(PREVIEW_BACKGROUND_URL);
          bgMetadata.layerName = localRepository.getParameter(PREVIEW_BACKGROUND_LAYERNAME);
          bgMetadata.subdomains = null;
          break;
        case "urlTileSet":
          bgMetadata.url = localRepository.getParameter(PREVIEW_BACKGROUND_URL);
          bgMetadata.subdomains = bgMetadata.url.includes("{s}") ?
                                  localRepository.getParameter(PREVIEW_BACKGROUND_SUBDOMAINS).split(",") : null;
          bgMetadata.layerName = null;
          break;
        }
      }
      return bgMetadata;
    }
    return null;
  }

  getAnalytics = (request: AnalyticsRequest): Promise<AnalyticsResponse[]> => {
    return axios({
      method: "POST",
      url: `${this.getStudioApiUrl()}/analytics`,
      data: request,
    })
        .then(r => {
          return r.data;
        });
  }

  saveSLDStyle = (styles: SLDStyle[], keywords?: string[]): Promise<any> => {
    const formData = new FormData();
    for (const style of styles) {
      const {title, sld} = style;
      formData.append("files", new File([new Blob([sld])], `${title}.sld`));
    }

    if (keywords?.length > 0) {
      formData.append("keywords", [...new Set(keywords)].join(','));
    }

    const headers = {
      "Content-Type": "application/octet-stream",
      ...(oidcProviderInfo?.enabled && {"Authorization": "Bearer " + oidcUtils.getOidcTokenValue()})
    };

    return axios.post(`${this.getStudioInternalApiUrl()}/upload/v2/files`, formData, {headers})
        .then(r => r.data);
  }

  uploadVirtualFiles = (files: { filename: string, data: string }[],
                        options?: { [key: string]: any }): Promise<any> => {
    const formData = new FormData();
    files.forEach(f => {
      formData.append("files", new File([new Blob([f.data])], `${f.filename}`));
    });
    for (const key in options) {
      if (options.hasOwnProperty(key)) {
        formData.append(key, JSON.stringify(options[key]));
      }
    }

    return axios({
      method: "POST",
      url: `${this.getStudioApiUrl()}/upload/files`,
      data: formData,
      headers: {"Content-Type": "application/octet-stream"}
    }).then(r => {
      return r.data;
    });
  }

  uploadVirtualFile = (filename: string, data: string, options?: { [key: string]: any }): Promise<any> => {
    return this.uploadVirtualFiles([{filename, data}], options).then(results => results?.[0]);
  }

  addServiceToCatalog = async (service: Service, products: PendingProductForService[]): Promise<any> => {
    const files: { filename: string; data: string }[] = [];

    for (const product of products) {
      const dataResp = await axios.get(`${this.getStudioApiUrl()}/products/${product.id}/data`);
      const firstDataId = dataResp?.data?.data?.[0]?.id;
      if (!firstDataId) {
        continue;
      }

      const rulesResp = await axios.get(`${this.getStudioApiUrl()}/data/${firstDataId}/access-rules`);
      const anonymousRule = rulesResp?.data?.accessRules?.find((rule: any) => rule.role === "LFN_ANONYMOUS");
      if (!anonymousRule) {
        continue;
      }

      let ogcFile = `url=${service.endpointUrl}\n`;

      switch (service.type) {
      case "wms":
      case "wmts":
        ogcFile += `layer=${product.name}`;
        files.push({
          filename: `service-${service.id} product-${product.id}.${service.type}`,
          data: ogcFile
        });
        break;
      case "wcs":
        ogcFile += `coverage=${product.name}`;
        files.push({
          filename: `service-${service.id} product-${product.id}.${service.type}`,
          data: ogcFile
        });
        break;
      case "wfs": {
        const svcProdResp = await axios.get(
            `${this.getStudioApiUrl()}/services/${service.id}/products/${product.id}`
        );
        const featureType =
            svcProdResp?.data?.publishedProduct?.publishInfo?.featureTypes?.[0]?.featureTypeName;
        if (featureType) {
          ogcFile += `featureType=${featureType}`;
          files.push({
            filename: `service-${service.id} product-${product.id}.${service.type}`,
            data: ogcFile
          });
        }
      }
        break;
      default:
        // unsupported service type: skip
        break;
      }
    }

    if (files.length > 0) {
      return this.uploadVirtualFiles(files, {dataRoot: {parent: null}});
    }
    return Promise.resolve({status: CatalogUpdatingMessageType.NO_ANONYMOUS_PRODUCTS});
  };

  removeServiceFromCatalog = (serviceId: string): Promise<any> => {
    if (!serviceId) {
      return Promise.resolve();
    }

    return axios.get(
        `${this.getStudioApiUrl()}/data/filter?page=1&pageSize=500&rsqlQuery=lfn.filePath%3D%3D%22%2Aservice-${serviceId}%2A%22`)
        .then(response => {
          const dataToRemove = response?.data?.data;
          if (dataToRemove.length > 0) {
            return axios.delete(`${this.getStudioApiUrl()}/data`, {data: dataToRemove.map(d => d.id)})
                .then(_ => null)
                .catch(this.notifyErrorHandlers);
          }
          return Promise.resolve();
        })
        .catch(this.notifyErrorHandlers);
  }

  removeEmptyUploadsFolders = (): Promise<any> => {
    const foldersToDelete = [];

    return Promise.allSettled([
      axios.get(`${this.getStudioApiUrl()}/folder/detailed?foldersOnly=true&page=1&pageSize=65536&parentId=ROOT`),
      this.getFusionDatastoreDirectory()
    ])
        .then(responses => {
          let datastoreDirectory: string = null;
          let folders = [];
          if (responses[0].status === 'fulfilled') {
            folders = (responses[0] as PromiseFulfilledResult<any>).value.data.results;
          }
          if (responses[1].status === 'fulfilled') {
            datastoreDirectory = (responses[1] as PromiseFulfilledResult<any>).value;
          }
          folders = folders.filter(f => f.path.startsWith(`folder=${datastoreDirectory}\\apolloup`));
          const folderRequests = [];
          folders.forEach(folder => {
            folderRequests.push(
                axios.get(`${this.getStudioApiUrl()}/folder?parentId=${folder.id}&page=1&pageSize=1`));
          })
          Promise.allSettled(folderRequests)
              .then(foldersResponses => {
                foldersResponses.forEach((folderResponse, index) => {
                  if (folderResponse.status === 'fulfilled') {
                    if ((folderResponse as PromiseFulfilledResult<any>).value.data.context.totalSize === 0) {
                      foldersToDelete.push(folders[index].id);
                    }
                  }
                })
                return axios.delete(`${this.getStudioApiUrl()}/data`, {data: foldersToDelete})
                    .then(_ => null)
                    .catch(this.notifyErrorHandlers);
              })
        })
  }

  getDataDetails = (id: string): any => {
    return axios({
      method: "GET",
      url: `${this.getStudioApiUrl()}/data/${id}`,
    }).then(r => {
      return r.data;
    });
  }

  batchUpdate = (id: string,
                 batchProperties: BatchProperties,
                 applyToFolders: boolean,
                 applyToImages: boolean,
                 applyToVectors: boolean,
                 applyToPointClouds: boolean,
                 applyToPanoramics: boolean,
                 applyToMeshes: boolean,
                 applyToDocuments: boolean,
                 directChildrenOnly: boolean) => {
    return axios.post(`${this.getStudioApiUrl()}/data/batch-update`, {
      updates: [
        {
          ids: [id],
          applyToChildren: !directChildrenOnly,
          categories: {
            "folders": applyToFolders,
            "images": applyToImages,
            "vectors": applyToVectors,
            "pointClouds": applyToPointClouds,
            "documents": applyToDocuments,
            "panoramics": applyToPanoramics,
            "meshes": applyToMeshes,
          },
          ...batchProperties,
        }
      ]
    })
        .then(_ => null);
  };

  private getRootFolder = (): Promise<any> => {
    const folders = [];
    folders.push(APOLLO_CATALOG_ROOT_FOLDER);
    return axios.get(`${this.getStudioApiUrl()}/folder/detailed?foldersOnly=true&page=1&pageSize=65536`)
        .then(r => {
          r.data.results.forEach((folder) => {
            if (folder.path.startsWith("folder=")) {
              folders.push({
                'id': folder.id,
                'title': folder.title,
                'path': folder.path,
                'parent': APOLLO_CATALOG_ROOT_FOLDER
              });
            }
          });
          return folders;
        })
        .catch((error) => {
          throw error;
        });
  }

  private getFolder = (folderId: string): Promise<any> => {
    return axios({
      method: "GET",
      url: `${this.getStudioApiUrl()}/folder/detailed?foldersOnly=true&page=1&pageSize=65536&parentId=${folderId}`,
    }).then(r => {
      return r.data.results;
    });
  }

  private getAllFldrs = (folderId?: string): Promise<any> => {
    return this.getRootFolder().then((rootFolder) => {
      const folders = rootFolder;
      return this.getPath(folderId).then((path) => {
        const parentsPromise = [];
        const parents = [];

        if (path.length > 1) {
          path.forEach((folder) => {
            if (folder !== "ROOT") {
              parentsPromise.push(this.getFolder(folder));
              parents.push(folder);
            }
          });
        }
        if (parentsPromise.length > 0) {
          return this.getChildFldrs(folderId, folders, parents, parentsPromise);
        } else {
          return folders;
        }
      });
    });
  }

  private getChildFldrs = (folderId: string, folders: any[], parents: string[],
                           parentsPromise: Promise<any>[]): Promise<any> => {
    return Promise.allSettled(parentsPromise).then((response) => {
      const results = response.filter((res) => res.status === 'fulfilled') as PromiseFulfilledResult<any>[];

      for (let i = 0; i < results.length; i++) {
        results[i].value.forEach((data) => {
          if (data.path.startsWith("folder=")) {
            if (folders.findIndex((folder) => folder.id === data.id) < 0) {
              folders.push({
                id: data.id,
                title: data.title ? data.title : data.name,
                path: data.path,
                parent: folders.find(p => p.id === parents[i]),
              });
            }
          }
        });
      }
      return folders;
    });
  }

  getPath = (folderId: string): Promise<any> => {
    return axios({
      method: "GET",
      url: `${this.getStudioApiUrl()}/folder/parents?id=${folderId}`,
    }).then(r => {
      return r.data;
    }).catch(_r => {
      const urlParams = new URLSearchParams(document.URL.substring(document.URL.lastIndexOf('?')));
      if (urlParams.has('folder')) {
        urlParams.delete('folder');
        window.history.replaceState({}, '',
            `${location.pathname}${Array.from(urlParams).length > 0 ? "?" + urlParams.toString() : ""}`
        );
        window.location.reload();
      }
      return null;
    });
  }

  getAllFolders = (folderId?: string): Promise<any> => {
    if (!folderId || folderId === APOLLO_CATALOG_ROOT_FOLDER.id) {
      return this.getRootFolder();
    } else {
      return this.getAllFldrs(folderId);
    }
  }

  getFolders = (folderId?: string): Promise<any> => {
    if (!folderId || folderId === APOLLO_CATALOG_ROOT_FOLDER.id) {
      return this.getRootFolder();
    } else {
      return this.getFolder(folderId);
    }
  }

  createFolder = (parentId: string, name: string): Promise<void> => {
    return axios({
      method: "POST",
      url: `${this.getStudioApiUrl()}/folder`,
      data: {
        folderName: name,
        parent: parentId !== "0" ? parentId : null
      }
    }).then(_ => null);
  }

  isFolder = (folderId: string): Promise<boolean> => {
    return axios({
      method: "GET",
      url: `${this.getStudioApiUrl()}/folder?page=1&pageSize=1&parentId=${folderId}`
    }).then(_r => {
      return true;
    }).catch(_r => {
      return false;
    })
  }

  getImportedDataPreviewMetadata = (data: SpatialModel) => {
    if (data.type === "folder") {
      return Promise.resolve({
        previewable: false,
        shouldForceToPreview: false,
        georeferenced: true,
        empty: true,
      });
    }
    return axios
        .get(`${this.getStudioInternalApiUrl()}${paths.PREVIEW_PATH}/preview-metadata/imported-data/${data.id}`)
        .then((response) => response.data) as Promise<PreviewMetadata>;
  };

  getFeatureTypesProperties = (capabilities: WFSCapabilities): Promise<FeatureTypesProperties> => {
    return Promise.resolve()
        .then(() => {
          const operation = capabilities.operations.find(o => o.name === "DescribeFeatureType");
          if (!operation.supportedFormats.some(f => f === "text/xml; subtype=gml/3.1.1")) {
            return {} as FeatureTypesProperties;
          }
          const request = (operation as any)?.supportedRequests.find(r => r.method === "GET");
          if (!request) {
            return {} as FeatureTypesProperties;
          }
          const wfsConfig = oidcProviderInfo?.enabled ?
              {
                headers: {
                  Authorization: "Bearer " + oidcUtils.getOidcTokenValue(),
                }
              } : {};
          return axios.get(`${request.url}service=WFS&request=DescribeFeatureType&version=2.0.0`, wfsConfig)
              .then((r) => {
                const result: FeatureTypesProperties = {};
                const xml = stringToXml(r.data);
                const featureTypesNames = capabilities.featureTypes.map(t => t.name);
                const schemas = this.getSchemas(xml, featureTypesNames);

                if (schemas.length > 0) {
                  return Promise.allSettled(schemas.map((url) => axios.get(url, wfsConfig)))
                      .then((response) => {
                        const results = response.filter(
                            (res) => res.status === 'fulfilled') as PromiseFulfilledResult<any>[];
                        if (results.length > 0) {
                          results.forEach((xmlDoc) => {
                            this.processDescribeFeatureType(stringToXml(xmlDoc.value.data), featureTypesNames, result);
                          })
                          return result;
                        } else {
                          throw new Error(
                              "ApolloAPI: Invalid WFS document (unable to read additional Schema documents).");
                        }
                      })
                      .catch(() => {
                        throw new Error("ApolloAPI: Invalid WFS document.");
                      });
                } else {
                  this.processDescribeFeatureType(xml, featureTypesNames, result);
                  return result;
                }
              });
        });
  };

  private getSchemas = (xml, featureTypesNames) => {
    const schemas = [];
    featureTypesNames.forEach((featureName) => {
      const schemaImport = xml.querySelector(
          `schema > import[namespace='${featureName.replace(/^.*:/, "")}']`);
      if (schemaImport) {
        const schemaLocation = schemaImport.getAttribute('schemaLocation');
        if (schemaLocation) {
          schemas.push(schemaLocation);
        }
      }
    });
    return schemas;
  }

  private processDescribeFeatureType = (xml: Document, featureTypesNames, result) => {
    featureTypesNames.forEach((featureTypeName) => {
      let tnsName = featureTypeName.replace(/^.*:/, "tns:");
      tnsName = tnsName.endsWith("Type") ? tnsName : `${tnsName}Type`;
      const elementNameType = xml.querySelector(`schema > element[type='${tnsName}']`);
      if (!elementNameType) {
        return new Error(
            "{\"id\":\"studio.style-editor.wfs-invalid-document\", \"defaultMessage\":\"Invalid WFS document.\"}");
      }
      if (!result[featureTypeName]) {
        result[featureTypeName] = [];
      }
      const elementName = elementNameType.getAttribute("name") + "Type";
      Array.from(xml.querySelectorAll(`schema > *[name='${elementName}'] element`)).forEach((p) => {
        const newElement = {
          name: p.getAttribute("name"),
          type: ApolloApiClass.mapFeatureTypePropertyType(p.getAttribute("type")),
        };
        if (!result[featureTypeName].includes(newElement)) {
          result[featureTypeName].push(newElement);
        }
      });
    });

  }

  addImportedData = (file: NewFileInfo) => {
    return axios
        .post(`${this.getStudioApiUrl()}/data`, file)
        .then((r) => r.data.data)
        .catch(this.notifyErrorHandlers) as Promise<ImportedData[]>;
  }

  getCrawlAccessRules = (): Promise<AccessRule[]> => {
    return axios.get(`${this.getStudioApiUrl()}/configuration`)
        .then((r) => {
          const settings = r.data.values;
          return JSON.parse(settings["crawlAccessRules"]);
        })
        .catch(this.notifyErrorHandlers) as Promise<AccessRule[]>;
  }

  getAllRolesWithDefaultAccessRules = (): Promise<AccessRule[]> => {
    // Called from Setting->Crawling Defaults page
    return this.getAllRoles()
        .then((roles: Role[]) => {
          return roles.map((n) => {
            return {
              role: n.role,
              permissions: [PermissionsType.READ]
            } as AccessRule
          })
        });
  }

  getAccessRules = (id: string): Promise<AccessRule[]> => {
    if (id === undefined || id === "") {
      const noAccessRights: AccessRule[] = [{
        role: ITEM_HAS_NO_RIGHTS,
        permissions: [PermissionsType.READ],
      }];
      return Promise.resolve(noAccessRights);
    } else {
      return axios
          .get(`${this.getStudioApiUrl()}/` + "data/" + id + "/access-rules")
          .then((r) => {
            return r.data.accessRules;
              }
          )
          .catch(this.notifyErrorHandlers);
    }
  }

  deleteAccessRules = (id: string, deletedRules: SecurityFormAccessRule[]) => {
    const resultPromises: Promise<any>[] = this.deleteDataItemAccessRules(id, deletedRules);
    return Promise.all(resultPromises).then(() => Promise.resolve());
  }

  private deleteDataItemAccessRules(id: string, accessRoles: SecurityFormAccessRule[]) {
    //Create an array of promises.
    return accessRoles.map(n => {
      if (n.applyToChildren) {
        return axios.delete(
            `${this.getStudioApiUrl()}/` + "data/" + id + "/bulk-access-rules/" + n.id)
            .catch(this.notifyErrorHandlers);
      } else {
        return axios.delete(
            `${this.getStudioApiUrl()}/` + "data/" + id + "/access-rules/" + n.id)
            .catch(this.notifyErrorHandlers);
      }
    });
  }

  setAccessRules = (id: string, accessRules: SecurityFormAccessRule[]) => {
    const resultPromises: Promise<any>[] = this.setDataItemAccessRules(accessRules, id);
    return Promise.all(resultPromises).then(() => Promise.resolve());
  }

  private setDataItemAccessRules(accessRules: SecurityFormAccessRule[], id: string) {
    return accessRules.map(n => {
      if (n.isNew) {
        const currentAccess = convertSecurityFormAccessRuleToAccessRule(n);

        if (n.applyToChildren) {
          return this.createAccessPermissionPromise(
              `${this.getStudioApiUrl()}/` + "data/" + id + "/bulk-access-rules", currentAccess);
        } else {
          return this.createAccessPermissionPromise(
              `${this.getStudioApiUrl()}/` + "data/" + id + "/access-rules", currentAccess);
        }
      } else {
        const currentAccess = convertSecurityFormAccessRuleToAccessRule(n);

        if (n.applyToChildren) {
          return this.createAccessPermissionPromise(
              `${this.getStudioApiUrl()}/` + "data/" + id + "/bulk-access-rules", currentAccess);
        } else {
          return this.updateAccessPermissionPromise(
              `${this.getStudioApiUrl()}/` + "data/" + id + "/access-rules/" + n.id, currentAccess);
        }
      }
    });
  }

  createAccessPermissionPromise = (url: string, payload: any): Promise<void> => {
    return axios
        .post(url, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }
  updateAccessPermissionPromise = (url: string, payload: any): Promise<void> => {
    return axios
        .put(url, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }

  isUserManagementActive = (): Promise<any> => {
    let isActive = true;
    this.getActuator("env").then((properties) => {
      const envObject = properties.propertySources.find(
          x => x.name.startsWith("Config resource") && x.name.endsWith("application-fusion.apollo.properties'"));
      isActive = envObject.properties["fusion.accessControl.enabled"].value === "true";
      return Promise.resolve(isActive);
    }).catch(() => {
      return Promise.resolve(isActive);
    });
    return Promise.resolve(isActive);
  }

  createRole = (role: Role): Promise<any> => {
    const payload: any = {
      "role": role.role,
      "description": role.description
    };
    return axios.post(`${this.getStudioApiUrl()}/roles`, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }

  getRole = (role: Role): Promise<any> => {
    return axios.get(`${this.getStudioApiUrl()}/roles/${role.role}`)
        .then(r => {
          return r.data
        })
        .catch(this.notifyErrorHandlers);
  }

  getUser = (user: User): Promise<any> => {
    return axios.get(`${this.getStudioApiUrl()}/users/${user.username}`)
        .then(r => {
          return r.data
        })
        .catch(this.notifyErrorHandlers);
  }
  deleteRole = (role: Role): Promise<any> => {
    return axios.delete(`${this.getStudioApiUrl()}/roles/${role.role}`)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }

  deleteUser = (user: User): Promise<any> => {
    return axios.delete(`${this.getStudioApiUrl()}/users/${user.username}`)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }

  createUser = (user: User): Promise<any> => {
    const payload: any = {
      "username": user.username,
      "password": user.userAuthCredentials,
      "roles": user.roles,
      "enabled": user.enabled
    };
    return axios.post(`${this.getStudioApiUrl()}/users`, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }

  updateRole = (role: Role): Promise<any> => {
    const payload: any = {
      "role": role.role,
      "description": role.description,
    };
    return axios.patch(`${this.getStudioApiUrl()}/roles/${role.role}`, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }
  updateUser = (user: User): Promise<any> => {
    const payload: any = {
      "username": user.username,
      "password": user.userAuthCredentials,
      "roles": user.roles,
      "enabled": user.enabled
    };
    return axios.patch(`${this.getStudioApiUrl()}/users/${user.username}`, payload)
        .then(_ => null)
        .catch(this.notifyErrorHandlers);
  }
  getAllUsers = (): Promise<User[]> => {
    return axios
        .get(`${this.getStudioApiUrl()}/` + "users")
        .then((r) => {
          const users = r.data;
          const usersList: User[] = users.map(n => {
            const user: User = {
              username: n.username,
              roles: n.roles,
              enabled: n.enabled
            }
            return user;
          });
          return usersList;
        }).catch(this.notifyErrorHandlers);
  }

  getCustomProperties = (): Promise<any> => {
    return axios({
      method: "GET",
      url: `${this.getStudioApiUrl()}/data/custom-properties`
    }).then(r => {
      return r.data;
    }).catch(_r => {
      return null;
    })
  }

  getAllRoles = (): Promise<Role[]> => {
    return axios
        .get(`${this.getStudioApiUrl()}/` + "roles")
        .then((r) => {
          const roleList: Role[] = r.data.map(n => ({role: n.role, description: n.description}));
          return roleList.sort((a: Role, b: Role) => {
            return (a.role.localeCompare(b.role));
              }
          );
        }).catch(this.notifyErrorHandlers);
  }

  downloadApolloMetadata = (id: string) => {
    let attachmentUrl = `${this.getStudioApiUrl()}/attachments/${id}/metadata`;
    axios
        .get(attachmentUrl)
        .then((r) => {
          const blob = new Blob([r.data], {type: 'text/xml'});
          attachmentUrl = URL.createObjectURL(blob);
          window.open(attachmentUrl);
          URL.revokeObjectURL(attachmentUrl); //Releases the resources
        });
  }

  getImageChainColorRamps = (): Promise<ColorRamp[]> => {
    return axios({
      method: "GET",
      url: `${this.getStudioApiUrl()}/styles/imagine/describe/colorramps`
    }).then(r => {
      const colorRamps: ColorRamp[] = r.data;
      colorRamps.sort((a, b) => a.name.localeCompare(b.name));
      return colorRamps;
    });
  }

  getImageChainProviders = (): Promise<IchConfig> => {
    return axios({
      method: "GET",
      url: `${this.getStudioApiUrl()}/styles/imagine/describe/providers`
    }).then(r => {
      const ichConfig: IchConfig = r.data;
      ichConfig.imageChainProviders.providers[0].imageChains.sort(
          (a, b) => {
            return a.title.localeCompare(b.title);
          });
      ichConfig.imageChainProviders.providers[0].imageChains.forEach((provider) => {
        provider.parameters.sort((a, b) => {
          return a.name.localeCompare(b.name);
        })
      });
      return ichConfig;
    });
  }

  private static mapFeatureTypePropertyType(type: string): FeatureTypePropertyType {
    switch (type) {
    case "xsd:int":
    case "xsd:long":
    case "xsd:decimal":
    case "xsd:short":
    case "xsd:float":
    case "xsd:double":
      return FeatureTypePropertyType.NUMERIC;
    case "xsd:string":
      return FeatureTypePropertyType.STRING;
    default:
      return null;
    }
  }

  getDatabaseConnections = (): Promise<DBConnectionConfig[]> => {
    return Promise.resolve([]);
  }

  AddFilesPromises = async (allFiles: Promise<AddResponse>[]) => {
    const results = await Promise.allSettled(allFiles);
    const errors = results.filter(result => result.status === 'rejected') as PromiseRejectedResult[];
    if (errors.length > 0) {
      let addFileErrors = "";
      errors.forEach(error => {
        const anyError = error.reason as any;
        const filePath = JSON.parse(anyError?.config?.data)?.filePath;
        addFileErrors += anyError.response.data + (filePath ? ` - ${filePath}` : "") + "<br>";
      })
      throw new Error(addFileErrors);
    }
    return null as Promise<AddResponse>;
  }

  addFiles = (files: NewFileInfo[]) => {
    const limit = pLimit(5);
    const allFiles = files.map(file => limit(() => axios
        .post(`${this.getStudioApiUrl()}/data`, file)
        .then((r) => null)
        .catch(this.notifyErrorHandlers) as Promise<AddResponse>));
    return this.AddFilesPromises(allFiles) as Promise<AddResponse>;
  }

  createDataRoot = (dataRoot: NewDataRoot) => {
    return axios
        .post(`${this.getStudioApiUrl()}/data-roots`, dataRoot)
        .then((r) => r.data.data)
        .catch(this.notifyErrorHandlers) as Promise<AddResponse>;
  }

  updateProperties = (updatedData: ImportedData): Promise<any> => {
    const update = {
      title: updatedData.title,
      abstractText: updatedData.abstractText,
      keywords: updatedData.keywords,
      customProperties: updatedData.customProperties,
    }
    return axios.patch(`${this.getStudioApiUrl()}/data/${updatedData.id}`, update)
        .then((r) => r.data.data)
        .catch(this.notifyErrorHandlers);
  }

}

export const createApolloApi = (serverConfiguration: ServerConfiguration) => new ApolloApiClass(serverConfiguration);